import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

export function LoadRemotePage(): Node {
  return html`
    <div>
      <h1>loadRemote</h1>
      <p>Load remote ES modules as components at runtime. This is the core API for building microfrontend architectures with onefold — compose independently deployed modules into a single host application.</p>

      <h2>What are Microfrontends?</h2>
      <p>Microfrontends split a large frontend into smaller, independently built and deployed pieces. Each team owns a "remote" module that the host application loads at runtime. onefold provides:</p>
      <ul>
        <li><strong>loadRemote</strong> — fetch and render remote modules dynamically</li>
        <li><strong>Isolation</strong> — sandbox remotes with Shadow DOM or iframes</li>
        <li><strong>Security</strong> — origin allowlisting, SRI integrity, and sandboxing</li>
        <li><strong>Communication</strong> — type-safe messaging between host and remotes</li>
      </ul>

      <h2>Basic Usage</h2>
      <p>The simplest way to load a remote module — provide a URL and an optional fallback:</p>
      ${CodeBlock(`import { html } from 'onefold';
import { loadRemote } from 'onefold/remote';

function App(): Node {
  return html\`
    <div>
      <h1>Host Application</h1>
      \${loadRemote({
        url: 'https://cdn.example.com/widgets/billing.js',
        fallback: () => html\`<p>Loading billing widget...</p>\`,
      })}
    </div>
  \`;
}`)}

      ${Callout('The remote module must export a default function that returns a Node. onefold calls it with the provided props.')}

      <h2>Remote Module Format</h2>
      <p>A remote is a standard ES module with a default export:</p>
      ${CodeBlock(`// billing-widget.ts (deployed separately)
import { html, createSignal } from 'onefold';

export default function BillingWidget(props: { plan: string }): Node {
  const expanded = createSignal(false);

  return html\`
    <div class="billing">
      <h3>Plan: \${props.plan}</h3>
      <button onclick=\${() => expanded.set(v => !v)}>Details</button>
      \${() => expanded() ? html\`<p>Billing details here...</p>\` : html\`<span></span>\`}
    </div>
  \`;
}`)}

      <h2>Passing Props</h2>
      ${CodeBlock(`loadRemote({
  url: 'https://cdn.example.com/widgets/billing.js',
  props: { plan: 'enterprise', userId: '42' },
  fallback: () => html\`<p>Loading...</p>\`,
})`)}

      <h2>Error Handling</h2>
      <p>Provide an <code>onError</code> handler for graceful degradation when a remote fails to load:</p>
      ${CodeBlock(`loadRemote({
  url: 'https://cdn.example.com/widgets/billing.js',
  fallback: () => html\`<p>Loading...</p>\`,
  onError: (err) => html\`<p class="error">Widget unavailable: \${err.message}</p>\`,
})`)}

      <h2>With Integrity Verification</h2>
      <p>Add SRI hashes to verify the remote hasn't been tampered with:</p>
      ${CodeBlock(`loadRemote({
  url: 'https://cdn.example.com/widgets/billing.js',
  integrity: 'sha384-oqVuAfXRKap7fdgcCY5uykM6+R9GqQ8K/ux...',
  fallback: () => html\`<p>Loading...</p>\`,
})`)}

      <h2>With Isolation</h2>
      <p>Isolate the remote's DOM and styles from the host:</p>
      ${CodeBlock(`loadRemote({
  url: 'https://cdn.example.com/widgets/legacy.js',
  isolation: 'shadow',  // Shadow DOM — styles don't leak in or out
  fallback: () => html\`<p>Loading...</p>\`,
})`)}

      <h2>Options Reference</h2>
      <table>
        <tr><th>Option</th><th>Type</th><th>Required</th><th>Description</th></tr>
        <tr><td><code>url</code></td><td>string</td><td>Yes</td><td>URL of the remote ES module.</td></tr>
        <tr><td><code>integrity</code></td><td>string</td><td>No*</td><td>SRI hash for verification.</td></tr>
        <tr><td><code>fallback</code></td><td>() => Node</td><td>No</td><td>UI shown while loading.</td></tr>
        <tr><td><code>onError</code></td><td>(err) => Node</td><td>No</td><td>UI shown on load failure.</td></tr>
        <tr><td><code>props</code></td><td>Record</td><td>No</td><td>Props passed to the remote component.</td></tr>
        <tr><td><code>isolation</code></td><td>'none' | 'shadow' | 'iframe'</td><td>No</td><td>DOM isolation mode.</td></tr>
        <tr><td><code>timeout</code></td><td>number</td><td>No</td><td>Override global timeout (ms) for this load.</td></tr>
      </table>

      <p>* Required when <code>configureSecurity({ requireIntegrity: true })</code> is set.</p>

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/microfrontends/isolation">Isolation Modes</a> — Shadow DOM vs iframe sandboxing</li>
        <li><a href="/microfrontends/communication">Communication</a> — messaging between host and remotes</li>
        <li><a href="/microfrontends/security">configureSecurity</a> — origin allowlisting and security perimeter</li>
        <li><a href="/microfrontends/sri">SRI Integrity</a> — verifying remote code hasn't been tampered with</li>
      </ul>
    </div>
  `;
}
